import { AuthService } from '@geovelo-frontends/commons';
import { Box, TextField } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import { Link, graphql, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import * as Yup from 'yup';

import { ForgotPasswordQuery } from '../../../graphql-types';
import Button from '../../components/button';
import Seo from '../../components/seo';
import { defaultLanguage } from '../../languages';
import SignInLayout from '../../layouts/sign-in';
import { TPageProps } from '../../page-props';

interface IFormValues {
  email: string;
}

function ForgotPasswordPage(props: TPageProps<ForgotPasswordQuery>) {
  const [emailSent, setEmailSent] = useState(false);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { isSubmitting, values, touched, errors, isValid, handleChange, handleSubmit } =
    useFormik<IFormValues>({
      initialValues: {
        email: '',
      },
      validationSchema: Yup.object().shape({
        email: Yup.string().required(t('commons.sign_in_form.email_required') || ''),
      }),
      enableReinitialize: true,
      onSubmit,
    });
  const snackbar = useSnackbar();

  useEffect(() => {
    if (!props.data.prismicForgotPassword)
      navigate(`/${defaultLanguage}${props.pageContext.slug.substring(3)}`);
  }, []);

  async function onSubmit({ email }: IFormValues, { setSubmitting }: FormikHelpers<IFormValues>) {
    setSubmitting(true);

    try {
      await AuthService.sendResetPasswordEmail(email);

      setEmailSent(true);
    } catch (err) {
      console.error(err);
      snackbar?.enqueueSnackbar(t('commons.forgot_password_form.server_error') || '');
      setSubmitting(false);
    }
  }

  const title = props.data.prismicForgotPassword?.data.title?.text;

  return (
    <>
      <Seo title={`${title || t('geovelo.pages_titles.forgot_password')}`} {...props} />
      <SignInLayout
        disableSSO
        data={
          emailSent
            ? {
                ...props.data.prismicForgotPassword?.data,
                form_title: undefined,
                form_description: undefined,
              }
            : props.data.prismicForgotPassword?.data
        }
        descriptionKey={
          emailSent
            ? 'commons.forgot_password_form.description_email_sent'
            : 'commons.forgot_password_form.description'
        }
        titleKey={
          emailSent
            ? 'commons.forgot_password_form.title_email_sent'
            : 'commons.forgot_password_form.title'
        }
      >
        {emailSent ? (
          <Box display="flex" flexDirection="column">
            <Button
              color="primary"
              component={Link}
              size="medium"
              to={`/${currentLanguage}/sign-in`}
              variant="outlined"
            >
              {t('geovelo.navigation.sign_in')}
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            display="flex"
            flexDirection="column"
            gap={2}
            onSubmit={handleSubmit}
          >
            <StyledTextField
              disabled={isSubmitting}
              error={Boolean(errors.email && touched.email)}
              helperText={touched.email && errors.email}
              id="email"
              margin="none"
              name="email"
              onChange={handleChange}
              placeholder={t('commons.sign_in_form.email') || ''}
              size="small"
              type="email"
              value={values.email}
              variant="filled"
            />
            <Button
              color="primary"
              disabled={isSubmitting || !isValid || !values.email}
              size="medium"
              type="submit"
              variant="contained"
            >
              {props.data.prismicForgotPassword?.data.cta_title?.text ||
                t('commons.forgot_password_form.actions.send')}
            </Button>
          </Box>
        )}
      </SignInLayout>
    </>
  );
}

const StyledTextField = styled(TextField)`
  &&& {
    .MuiInputBase-root {
      &::before,
      &::after {
        border-bottom: 0 !important;
      }

      border-radius: 8px;

      input {
        padding-bottom: 12px;
        padding-top: 12px;
      }
    }
  }
`;

export const query = graphql`
  fragment ForgotPasswordData on PrismicForgotPasswordData {
    title {
      text
    }
    form_title {
      text
    }
    form_description {
      text
    }
    cta_title {
      text
    }
    image {
      gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
    }
  }
`;

export default ForgotPasswordPage;
